import { useCallback, useEffect, useState } from "react";
import { History, Loader2, ShieldAlert, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { assetUrl } from "@/lib/api";

const usd = (v) => `${(v ?? 0).toFixed(2).replace(".", ",")} $`;
const err = (e) => e?.response?.data?.detail || e?.message || "Errore";
const QA_TONE = { verified: "text-emerald-700 bg-emerald-50", manual: "text-emerald-700 bg-emerald-50", technical: "text-sky-700 bg-sky-50",
  unverified: "text-amber-800 bg-amber-50", none: "text-slate-600 bg-slate-100" };
const usable = (code) => ["verified", "manual", "technical", "none"].includes(code);

function QaBadge({ qa, testid }) {
  const Icon = qa.code === "unverified" ? ShieldAlert : ShieldCheck;
  return (
    <div className={`rounded-md px-2 py-1.5 text-xs ${QA_TONE[qa.code]}`} data-testid={testid}>
      <span className="inline-flex items-center gap-1 font-medium"><Icon size={13} />{qa.label}</span>
      {qa.qa?.issues?.length > 0 && <p className="mt-0.5">{qa.qa.issues.join(" · ")}</p>}
      {qa.qa?.vision?.summary && <p className="mt-0.5 opacity-80">AI: {qa.qa.vision.summary} ({qa.qa.vision.relevance}/10)</p>}
    </div>
  );
}

function HistoryList({ items, current, onRestore }) {
  const others = items.filter((h) => h.url !== current);
  if (!others.length) return null;
  return (
    <div data-testid="image-history">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><History size={12} />Versioni di questa immagine</p>
      <div className="mt-1.5 grid grid-cols-3 gap-2">
        {others.map((h, i) => (
          <div key={h.id} className="rounded border overflow-hidden" data-testid={`image-history-item-${i}`}>
            <img src={assetUrl(h.url)} alt="" className="aspect-[4/3] w-full object-cover" />
            <div className="p-1 space-y-1">
              <p className="text-[10px] text-slate-500 truncate">{h.qa.label}</p>
              <Button size="sm" variant="outline" className="h-6 w-full text-[11px]" disabled={!usable(h.qa.code)} onClick={() => onRestore(h.url)}
                data-testid={`image-history-restore-${i}`}>Ripristina</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Result({ job, onUse, busy }) {
  if (job.status === "running") return <p className="text-xs text-slate-600 inline-flex items-center gap-1.5" data-testid="image-regen-running"><Loader2 size={13} className="animate-spin" />Generazione e controllo qualità in corso…</p>;
  if (job.status === "error") return <p className="text-xs text-red-700 bg-red-50 rounded px-2 py-1.5" data-testid="image-regen-error">{job.error}</p>;
  const a = job.result.asset;
  return (
    <div className="rounded-lg border p-2 space-y-2" data-testid="image-regen-result">
      <img src={assetUrl(a.url)} alt="" className="w-full max-h-56 object-contain bg-slate-50 rounded" />
      <QaBadge qa={job.result.qa} testid="image-regen-qa" />
      <Button size="sm" disabled={busy || !usable(job.result.qa.code)} onClick={() => onUse(a.url)} data-testid="image-regen-use-btn">Usa questa immagine</Button>
    </div>
  );
}

export default function ImageDetailsTab({ api, projectId, url, onPick }) {
  const [info, setInfo] = useState(null);
  const [note, setNote] = useState("");
  const [ok, setOk] = useState(false);
  const [client, setClient] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => api.get(`/projects/${projectId}/images/info`, { params: { url } }).then(({ data }) => setInfo(data)).catch((e) => setMsg(err(e))), [api, projectId, url]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(() => api.get(`/jobs/${job.id}`).then(({ data }) => data.status !== "running" && (setJob({ ...data, id: job.id }), load())).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [job, api, load]);
  if (!info) return <p className="py-6 text-xs text-slate-500" data-testid="image-details-loading">{msg || "Caricamento…"}</p>;
  const isClient = info.provenance.kind === "client";
  const left = info.attempts.max - info.attempts.used;
  const canRegen = info.paid_enabled && !info.blocked && left > 0 && ok && (!isClient || client) && info.estimate.total_usd <= info.budget.remaining_usd && job?.status !== "running";
  const start = async () => {
    setMsg("");
    try {
      const { data } = await api.post(`/projects/${projectId}/images/regenerate`, { url, instructions: note, confirm_usd: info.estimate.total_usd, replace_client_photo: client });
      setJob({ id: data.job_id, status: "running" });
    } catch (e) { setMsg(err(e)); }
  };
  const use = async (to) => {
    setBusy(true); setMsg("");
    try { await api.post(`/projects/${projectId}/images/apply`, { from_url: url, to_url: to }); onPick(to, { source: "ai_edit" }); } catch (e) { setMsg(err(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 py-2" data-testid="image-details-tab">
      <div className="flex gap-3">
        <img src={assetUrl(url)} alt="" className="h-24 w-32 shrink-0 rounded object-cover bg-slate-100" />
        <div className="min-w-0 space-y-1.5 text-xs">
          <p data-testid="image-provenance"><b>Provenienza:</b> {info.provenance.label}</p>
          <p className="text-slate-600" data-testid="image-used-in">Usata in: {info.used_in.map((w) => `${w.page} › ${w.section}`).join(", ") || "—"}</p>
          <QaBadge qa={info.qa} testid="image-qa-status" />
        </div>
      </div>
      <HistoryList items={info.history} current={url} onRestore={use} />
      <div className="rounded-lg border p-3 space-y-2" data-testid="image-regen-box">
        <p className="text-sm font-medium inline-flex items-center gap-1.5"><Sparkles size={14} />Rigenera con istruzioni mirate</p>
        {info.blocked && <p className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1.5" data-testid="image-regen-blocked">{info.blocked}</p>}
        {!info.paid_enabled && <p className="text-xs text-amber-800 bg-amber-50 rounded px-2 py-1.5" data-testid="image-regen-paid-disabled">Generazione a pagamento disattivata: serve un'autorizzazione esplicita nelle Impostazioni.</p>}
        <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 400))} rows={2} placeholder="Es. più luminosa, inquadratura dall'alto, toni caldi" data-testid="image-regen-instructions" />
        <p className="text-[11px] text-slate-600" data-testid="image-regen-cost">Costo massimo {usd(info.estimate.total_usd)} (immagine + controllo qualità) · disponibili nel budget del sito {usd(info.budget.remaining_usd)} · rigenerazioni rimaste {left}/{info.attempts.max}</p>
        {isClient && <label className="flex items-center gap-2 text-xs"><Checkbox checked={client} onCheckedChange={(v) => setClient(!!v)} data-testid="image-regen-client-checkbox" />È una foto del cliente: confermo di volerla sostituire con un'immagine AI</label>}
        <label className="flex items-center gap-2 text-xs"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="image-regen-confirm-checkbox" />Confermo il costo massimo indicato</label>
        <Button size="sm" disabled={!canRegen} onClick={start} data-testid="image-regen-btn">Rigenera immagine</Button>
        {job && <Result job={job} onUse={use} busy={busy} />}
      </div>
      {msg && <p className="text-xs text-red-700" data-testid="image-details-error">{msg}</p>}
    </div>
  );
}
